module API.FormType where

import Common

import API.FormBase (FormBase, FormBasic, FormID)
import Control.Plus (empty)
import Data.Argonaut as J
import Data.Argonaut.Decode.Decoders as JD
import Data.Array (concat, filter)
import Data.Array as Arr
import Data.Either (hush)
import Data.List as List
import Data.String as Str
import Data.Tree (Tree, mkTree)
import Error (ErrorContext(..), sentryFlag, tagContext)
import Foreign.Object (Object, toArrayWithKey)
import Foreign.Object as O
import GraphQL (queryStringParams)
import GraphQL.Join (StringQuery, gqlQuery, runGraphQL)
import GraphQL.Query (Mutation, Query, buildMutation, buildQuery, nameQuery)
import GraphQL.Type (NamedType, namedType)
import Internal.Luxon as Luxon
import Internal.SelectionMap (SelectionMap)
import Internal.SelectionMap as Sel
import Widgets.Form.AutoFillSubForm (courseSubForm, employerSubForm)
import Widgets.Form.Checkbox (Checkbox(..))
import Widgets.Form.Class (getId)
import Widgets.Form.Date (DateInput(..), DateTimeInput(..), parseDate)
import Widgets.Form.Dynamic (DynFormWidget, wrapFormWidget)
import Widgets.Form.EmployerSelect (initEmployerSelect)
import Widgets.Form.Expectations (courseExpectationsWidget, employerExpectationsWidget)
import Widgets.Form.List (simpleListWidget)
import Widgets.Form.NumberInput (NumberInput(..))
import Widgets.Form.PhoneInput (PhoneInput(..))
import Widgets.Form.Radio (Radio(..))
import Widgets.Form.Select (initSelectWidget)
import Widgets.Form.Simple (FormError(..), Label(..))
import Widgets.Form.StudentSubForm (StudentSubForm(..))
import Widgets.Form.SubForm (SubForm(..))
import Widgets.Form.TextInput (TextArea(..), TextInput(..))
import Widgets.Signature.Types (formSigFieldFields)


type FormPickerMap = SelectionMap FormID FormBasic

emptyFormPickerMap :: FormPickerMap
emptyFormPickerMap = Sel.empty

isFormSelected :: FormBasic -> FormPickerMap -> Boolean
isFormSelected form fpm = Sel.isSelected form.id fpm

getSelectedForm :: FormID -> FormPickerMap -> Maybe FormBasic
getSelectedForm = Sel.getSelected

selectForm :: FormBasic -> FormPickerMap -> FormPickerMap
selectForm form fpm = Sel.select form.id form fpm

deselectForm :: FormBasic -> FormPickerMap -> FormPickerMap
deselectForm form fpm = Sel.deselect form.id fpm

selectForms :: forall f. Functor f => Foldable f =>
    f FormBasic -> Boolean -> FormPickerMap -> FormPickerMap
selectForms forms select fpm = Sel.selectAll forms' fpm select
  where
    forms' = map (\f -> Tuple f.id f) forms

areFormsSelected :: forall f. Functor f => Foldable f => f FormBasic -> FormPickerMap -> Boolean
areFormsSelected = Sel.areAllSelected <<< map _.id

type FormType = FormBase
    ( fields :: Array DynFormWidget
    -- ^ FormWidgets to be rendered into inputs and save data to documents in
    -- our WorkQueue.
    , sections :: Array (Tree FormSection)
    )

-- | Defines a section within a form that can be displayed in the WorkQueue.
type FormSection =
    { id :: String
    -- ^ ID of the section. Same as the subform ID. Used to jump to that
    -- section via anchor.
    , workQueueLabel :: String
    -- ^ We want to use the label specifically for the workQueue.
    }

-- | Sets the user's preference for form order on the backend
setFormOrder :: Array FormID -> Mutation Unit
setFormOrder ids = nameQuery "SetFormOrder" $ buildMutation "setUserOption"
    { input: namedType { form_type_order: ids } :: NamedType "UserOptionsInput" _ }

-- | Fetches a list of basic info about available form types with no layout
-- | info.
-- |
-- | The layout info is quite heavy and must be requested individually as
-- | needed, not all at once.
formListQuery :: Query (Array FormBasic)
formListQuery = nameQuery "FormTypeList" $ buildQuery "form_types" {}

formsQuery :: String
formsQuery = "query { form_types"
    <> formTypeFields false
    <> "}"

formQueryRequest :: FormID -> StringQuery FormType
formQueryRequest id = gqlQuery ("""
query FormType""" <> queryStringParams [ "$id: ID!" ] <> """ {
    form_type""" <> queryStringParams [ "id: $id" ] <> """ {
        """ <> formTypeFields true <> """
    }
}""") { id } extractFormType

formQuery :: FormID -> Aff (Either ErrorWrapper FormType)
formQuery = runGraphQL <<< formQueryRequest

extractFormType :: Json -> Either ErrorWrapper FormType
extractFormType json = do
    obj <- wrapJsonError' json (decodeJObject json >>= (_ .: "data") >>= (_ .: "form_type"))
    attemptDecodeFormType obj


formTypeFields :: Boolean -> String
formTypeFields fields = """
    id
    type
    abbr
    bgColor
    label
    priority
    signatures { """ <> formSigFieldFields <> """ }
    mutations {
        create
        update
        delete
    }
    """ <> (if fields then "fields" else "") <> """
"""

attemptDecodeFormType :: Json -> Either ErrorWrapper FormType
attemptDecodeFormType json = do
    obj <- wrapJsonError' json $ decodeJObject json
    fields <- decodeFormFields obj

    wrapJsonError' json do
        id <- obj .: "id"
        type' <- obj .: "type"
        abbr <- obj .: "abbr"
        bgColor <- obj .: "bgColor"
        label <- obj .: "label"
        priority <- obj .: "priority"
        mutations <- obj .: "mutations"
        signatures <- obj .: "signatures"
        sections' <- decodeSections Nothing obj

        let workQueueLabel = if length signatures > 0
                then lookupTranslation "preview-and-sign"
                else lookupTranslation "preview"
            sections = Arr.snoc sections' $
                mkTree { id: "signature-anchor", workQueueLabel } empty

        pure {
            id,
            type: type',
            abbr,
            bgColor,
            label,
            priority,
            mutations,
            fields,
            signatures,
            sections
        }


-- We process sections separately so we don't have to implement all this logic
-- inside the field parsing code.
decodeSections :: Maybe String -> Object Json -> Either JsonDecodeError (Array (Tree FormSection))
decodeSections context json = do
    fields <- getField decodePhpJObj json "fields"
    concat <$> traverse (decodeSection context) (toArrayWithKey Tuple fields)

decodeSection :: Maybe String -> Tuple String Json -> Either JsonDecodeError (Array (Tree FormSection))
decodeSection context (Tuple key json) = do
    type' <- decodeJObject json >>= (_ .: "type")

    matchSectionWidgets type'
  where
    id = case context of
        Just parent -> parent <> "." <> key
        Nothing -> key

    defaultLabel = "No Label"

    decodeFormSection = do
        value <- decodeJObject json >>= (_ .: "value")
        workQueueLabel <- value .:? "workQueueLabel" .!= defaultLabel
        section <- value .:? "section" .!= false

        pure {
            value,
            workQueueLabel,
            section
        }

    fromFS { workQueueLabel } = { id, workQueueLabel }

    checkValidFormSection fs = fs.section && fs.workQueueLabel /= defaultLabel

    matchSectionWidgets "Label" = do
        fs <- decodeFormSection

        if checkValidFormSection fs
            then pure [ mkTree (fromFS fs) empty ]
            else pure []
    matchSectionWidgets type'
        | Str.contains (Str.Pattern "SubForm") type' = doDecode
        | otherwise = pure []

    doDecode = do
        fs <- decodeFormSection
        subforms <- decodeSections (Just id) fs.value

        if checkValidFormSection fs
            then pure [ mkTree (fromFS fs) (List.fromFoldable subforms) ]
            else pure subforms


-- List of special hidden widgets that the backend uses, but we don't render.
hiddenWidgets :: Array String
hiddenWidgets = [ "ID", "Hidden" ]

notHiddenWidget :: Json -> Boolean
notHiddenWidget obj = fromMaybe true $ hush $ do
    type' <- (decodeJObject obj) >>= (_ .: "type")
    pure $ not $ Arr.elem type' hiddenWidgets

-- This is a hack to work around the fact that PHP doesn't properly distinguish
-- between empty arrays and empty objects, even when serializing to JSON,
-- which does make that distinction.
decodePhpJObj :: Json -> Either JsonDecodeError (Object Json)
decodePhpJObj json | json == J.fromArray [] =
    pure O.empty
decodePhpJObj json =
    decodeJObject json

decodeFormFields :: Object Json -> Either ErrorWrapper (Array DynFormWidget)
decodeFormFields obj = do
    fields <- wrapJsonError $ getField decodePhpJObj obj "fields"
    traverse (\ a@(Tuple key json) -> handleParseErrors key json $ toFormWidgetState a) $
        filter (\(Tuple _ j) -> notHiddenWidget j) $
        toArrayWithKey Tuple fields
  where
    handleParseErrors
        :: String
        -> Json
        -> Either ErrorWrapper DynFormWidget
        -> Either ErrorWrapper DynFormWidget
    handleParseErrors _ _ (Right widget) = Right widget
    -- If sentry is enabled, crash on parse errors and report the issue to sentry.
    handleParseErrors key json (Left err) | sentryFlag =
        tagContext (LoadingJson ("form widget " <> key) json) $
        Left err
    -- If sentry is disabled (i.e., in dev environments) display error widgets in the form.
    handleParseErrors key _ (Left err) =
        pure $ wrapFormWidget $ FormError { id: key, err }


toFormWidgetState :: Tuple String Json -> Either ErrorWrapper DynFormWidget
toFormWidgetState (Tuple key json) = do
        obj <- wrapJsonError $ decodeJObject json

        type' <- wrapJsonError $ obj .: "type"
        list <- wrapJsonError $ obj .: "list"
        let maybeLabel = (obj .: "value") >>= decodeJObject >>= (_ .: "label")
        let label = case maybeLabel of
                Right lbl -> lbl
                Left _ -> ""

        widget <- matchWidget key obj type'
        if list
            then pure $ wrapFormWidget $ simpleListWidget
                    -- TODO: class on list widgets?
                { id: getId widget, label, template: widget, cls: "" }
            else pure widget

matchWidget :: String -> Object Json -> String -> Either ErrorWrapper DynFormWidget
matchWidget key obj widgetName | Str.contains (Str.Pattern "SubForm") widgetName = do
    v <- wrapJsonError $ obj .: "value"

    section <- wrapJsonError $ v .: "section"
    collapsible <- wrapJsonError $ v .: "collapsible"
    expanded <- wrapJsonError $ v .: "expanded"
    label <- wrapJsonError $ v .:? "label"
    let defaultLabelClass = if section then "label-section" else "label-default"
    labelClass <- wrapJsonError $ v .:? "labelClass" .!= defaultLabelClass
    cls <- wrapJsonError $ v .:? "class" .!= ""
    fields <- decodeFormFields v

    let subform = SubForm {
        id: key,
        label,
        labelClass,
        section,
        collapsible,
        expanded,
        cls,
        fields
    }
    case widgetName of
        "SubForm" -> pure $ wrapFormWidget subform
        "StudentSubForm" -> pure $ wrapFormWidget $ StudentSubForm subform
        "EmployerSubForm" -> pure $ wrapFormWidget $ employerSubForm subform
        "CourseSubForm" -> pure $ wrapFormWidget $ courseSubForm subform
        _ -> wrapJsonError $ Left (AtKey "type" (UnexpectedValue (encodeJson widgetName)))

matchWidget key obj widgetName | Str.contains (Str.Pattern "AutoFillSelect") widgetName = wrapJsonError do
    value <- obj .: "value"

    label <- value .: "label"
    cls <- value .:? "class" .!= ""
    options <- value .: "options"
    placeholder <- value .:? "placeholder"
    default <- value .:? "default" .!= J.fromString ""

    Right $ wrapFormWidget $ initSelectWidget {
        id: key,
        label, cls,
        options,
        placeholder,
        value: Just default
    }

matchWidget key obj widgetName | Str.contains (Str.Pattern "Expectations") widgetName = do
    v <- wrapJsonError $ obj .: "value"

    label <- wrapJsonError $ v .: "label"
    cls <- wrapJsonError $ v .:? "class" .!= ""

    fields <- decodeFormFields v
    let subform = SubForm {
        id: key <> "-entry",
        label: Nothing,
        labelClass: "",
        section: false,
        collapsible: false,
        expanded: true,
        cls,
        fields
    }

    expectationsWidget <- wrapJsonError $ case widgetName of
            "Expectations" -> Right courseExpectationsWidget
            "CourseExpectations" -> Right courseExpectationsWidget
            "EmployerExpectations" -> Right employerExpectationsWidget
            _ -> Left (AtKey "type" (UnexpectedValue (encodeJson widgetName)))

    Right $ expectationsWidget subform {
        id: key,
        label, cls
    }

matchWidget key obj "EmployerSelect" = wrapJsonError do
    v <- obj .: "value"

    label <- v .: "label"
    cls <- v .:? "class" .!= ""
    options <- JD.getFieldOptional' decodePhpJObj obj "options" .!= O.empty
    contact <- options .:? "contact" .!= false

    Right $ wrapFormWidget $ initEmployerSelect
        { id: key
        , label
        , cls
        , contact
        }

matchWidget key obj "Date" = wrapJsonError do
    v <- obj .: "value"

    label <- v .: "label"
    cls <- v .:? "class" .!= ""
    value <- v .:? "default"

    Right $ wrapFormWidget $ DateInput {
        id: key,
        label, cls,
        value: parseDate =<< value
    }

matchWidget key obj "Telephone" = wrapJsonError do
    v <- obj .: "value"

    label <- v .: "label"
    cls <- v .:? "class" .!= ""
    placeholder <- v .:? "placeholder"
    value <- v .:? "default" .!= ""

    Right $ wrapFormWidget $ PhoneInput {
        id: key,
        label, cls,
        placeholder,
        value
    }

matchWidget key obj "Radio" = wrapJsonError do
    v <- obj .: "value"

    label <- v .: "label"
    cls <- v .:? "class" .!= ""
    options <- v .: "options"
    value <- v .:? "default"

    Right $ wrapFormWidget $ Radio {
        id: key,
        label, cls,
        options,
        value
    }

matchWidget key obj "Checkbox" = wrapJsonError do
    v <- obj .: "value"

    label <- v .: "label"
    cls <- v .:? "class" .!= ""
    value <- v .:? "default" .!= false

    Right $ wrapFormWidget $ Checkbox {
        id: key,
        label, cls,
        -- If there's a list of options present, use the legacy yes/no behavior.
        legacy: O.member "options" v,
        value
    }

matchWidget key obj "Number" = wrapJsonError do
    v <- obj .: "value"

    label <- v .: "label"
    cls <- v .:? "class" .!= ""
    placeholder <- v .:? "placeholder"
    step <- v .: "step"
    value <- v .:? "default"

    Right $ wrapFormWidget $ NumberInput {
        id: key,
        label, cls,
        step,
        placeholder,
        value
    }

matchWidget key obj "Label" = wrapJsonError do
    v <- obj .: "value"

    label <- v .: "label"
    cls <- v .:? "class" .!= ""

    Right $ wrapFormWidget $ Label {
        id: key,
        label, cls
    }

matchWidget key obj "Text" = wrapJsonError do
    v <- obj .: "value"

    label <- v .: "label"
    cls <- v .:? "class" .!= ""
    placeholder <- v .:? "placeholder"
    value <- v .:? "default" .!= ""

    Right $ wrapFormWidget $ TextInput {
        id: key,
        label, cls,
        placeholder,
        value
    }

matchWidget key obj "Select" = wrapJsonError do
    value <- obj .: "value"

    label <- value .: "label"
    cls <- value .:? "class" .!= ""
    options <- value .: "options"
    placeholder <- value .:? "placeholder"
    default <- value .:? "default" .!= J.fromString ""

    Right $ wrapFormWidget $ initSelectWidget {
        id: key,
        label, cls,
        options,
        placeholder,
        value: Just default
    }

matchWidget key obj "TextArea" = wrapJsonError do
    v <- obj .: "value"

    label <- v .: "label"
    cls <- v .:? "class" .!= ""
    placeholder <- v .:? "placeholder" .!= Nothing
    value <- v .:? "default" .!= ""

    Right $ wrapFormWidget $ TextArea {
        id: key,
        label, cls,
        placeholder,
        value
    }

matchWidget key obj "DateTime" = wrapJsonError do
    v <- obj .: "value"

    label <- v .: "label"
    cls <- v .:? "class" .!= ""
    value <- v .:? "default"

    Right $ wrapFormWidget $ DateTimeInput {
        id: key,
        label, cls,
        value: Luxon.fromISO =<< value
    }

-- We aren't guaranteed to get a parsed type that we support. This error
-- should make it's way to the console or better Sentry so it can be
-- caught if it ever happens in production.
matchWidget _ _ type' =
    Left $ DecodeError $ AtKey "type" $ UnexpectedValue $ encodeJson type'
